import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { GeoCity } from '../../../../entities/hydra';
import { useAppSelector } from 'src/redux/hooks';
import { getMany } from 'src/redux/features/dynamic-hydra/actions';
import { INestedWhereCommand } from '@llws/dynamic-router-utils';
import { createOrderByCommand, createWhereCommand } from 'src/app-utils/helpers';
import { Nullable } from '@jamesgmarks/utilities';

interface GeoCityDropdownProps {
  cityId: Nullable<number>;
  onCityChange: (city: Nullable<GeoCity>) => void;
  label?: string;
  disabled?: boolean;
  fullWidth?: boolean
  required?: boolean;
}

const GeoCityDropdown: React.FC<GeoCityDropdownProps> = ({
  cityId,
  onCityChange,
  label = "City",
  disabled = false,
  fullWidth = false,
  required = true,
}) => {
  const cities = useAppSelector(state => state.dynamicHydra.data.geo_cities?.list ?? []);

  const [search, setSearch] = useState('');
  const [selectedCity, setSelectedCity] = useState<Nullable<GeoCity>>(null);
  const [initialLoad, setInitialLoad] = useState(false);

  useEffect(() => {
    const searchTerm = search.split(',')[0].trim();
    const whereCommands: INestedWhereCommand[] = [
      ...(
        searchTerm
          ? [{
            condition: 'or' as const,
            whereCommands:
              [
                createWhereCommand('gc.cityName', `${searchTerm}`, 'like'),
              ],
          }]
          : []
      ),
      ...(
        cityId
          ? [{
            condition: 'or' as const,
            whereCommands:
              [
                createWhereCommand('gc.id', `${cityId}`, 'in'),
              ],
          }]
          : []
      ),
    ];
    getMany('geo_cities', {
      alias: 'gc',
      relations: [
        { path: 'gc.geoProvince', alias: 'gp' },
        { path: 'gp.geoCountry', alias: 'geoCountry' },
      ],
      where: whereCommands,
      limit: 25,
      orderBy: [createOrderByCommand('asc', 'gc.cityName')],
    });
  }, [search, cityId]);

  const handleCityChange = React.useCallback((newValue: GeoCity | null) => {
    setSelectedCity(newValue);
    onCityChange(newValue);
  }, [onCityChange]);

  useEffect(() => {
    if (cityId && cities.length && !initialLoad) {
      const city = cities.find((city) => city.id === cityId);
      if (city) {
        handleCityChange(city);
      }
      setInitialLoad(true);
    }
  }, [cityId, cities, initialLoad, handleCityChange]);

  return (
    <Autocomplete
      options={cities}
      fullWidth={fullWidth}
      getOptionLabel={(option) =>
        `${option.cityName}, `
        + `${option.geoProvince?.provinceName ?? ''}, `
        + `${option.geoProvince?.geoCountry.countryName ?? ''}`
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} >
          <Typography>
            <b>{option.cityName}</b>
            {option.geoProvince && (
              <> — {option.geoProvince.provinceName}, {option.geoProvince.geoCountry.countryName}</>
            )}
          </Typography>
        </Box>
      )}
      value={selectedCity}
      onChange={(_, newValue) => handleCityChange(newValue ?? null)}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onInputChange={(_, newInputValue) => setSearch(newInputValue)}
      renderInput={(params) => (
        <TextField required={required} {...params} label={label} placeholder="Search for a city..." />
      )}
      disabled={disabled}
      filterOptions={x => x} //prevents the component from filtering on the frontend
    />
  );
};

export default GeoCityDropdown;